import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useMutation, useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useHistory } from 'react-router';
import { observer } from 'mobx-react-lite';
import { ConfirmActionDialog, ConfirmActionDialogState } from 'components/Dialogs';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import IconButton from '@material-ui/core/IconButton';
import PersonIcon from '@material-ui/icons/Person';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { useStyles } from './styles';
import { CircularProgress, Paper, Tooltip, Grid, Container } from '@material-ui/core';
import PreviewIcon from '@material-ui/icons/RemoveRedEyeSharp';
import { orange } from '@material-ui/core/colors';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { getRowsPerPageOptions } from 'utils/pagination';
import { Switcher } from 'components/Switcher';
import EmailIcon from './../../assets/icons/EndUser/Email.svg';
// import { toFormatDateTime } from 'utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useIsMobile } from 'hooks/useResponsive';
interface UsersTableProps {
  page: number;
  setPage: (page: number) => void;
}

export const UsersTable: FC<UsersTableProps> = observer(({ page, setPage }) => {
  const history = useHistory();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const {
    ldapStore: { usersView, getUsers, setClickedUser, deleteUser, lockUser, clickedUser },
    dashboardStore: { getAppStatistic },
    notificationStore: { openNotification },
  } = useStore();
  const [data, setData] = useState<any>({});
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState<ConfirmActionDialogState>({
    open: false,
  });
  const [openConfirmLockDialog, setOpenConfirmLockDialog] = useState<ConfirmActionDialogState>({
    open: false,
  });
  const [notifyStatus, setNotifyStatus] = useState<boolean>(true);
  const [appStatus, setAppStatus] = useState<boolean>(false);
  const getAppStatisticReq = useQuery('getAppStatisticReq', () => getAppStatistic(), {
    onError: () => openNotification('error', `Can't get statistic`),
  });
  const appStatistics = getAppStatisticReq?.data?.data?.totals;
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userDataFromUrl = urlParams.get('userData');
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      localStorage.setItem('token', `Bearer ${tokenFromUrl}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (userDataFromUrl) {
      try {
        const parsedUserData = JSON.parse(decodeURIComponent(userDataFromUrl));
        localStorage.setItem('Profile', JSON.stringify(parsedUserData));
        setData(parsedUserData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.setItem('profile', userDataFromUrl);
        setData(userDataFromUrl);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const profile = JSON.parse(localStorage.getItem('Profile') as string) || data;
  const permission = profile?.permission;
  const endUserConfigSettings = JSON.parse(localStorage.getItem('endUserConfigSettings') as string);
  const userId = endUserConfigSettings?.userId;
  const deleteUserReq = useMutation((dn: string) => deleteUser(dn, appStatus, permission), {
    mutationKey: 'deleteUserReq',
    onSuccess: () => {
      openNotification('success', `Deleted succcessfully`);
      setLoading(true);
      isFetching.current = true;
      getAppStatisticReq.refetch();
      getUsers().finally(() => {
        setLoading(false);
        isFetching.current = false;
      });
    },
    onError: () => openNotification('error', `Can't delete user`),
  });
  const lockUserReq = useMutation((dn: string) => lockUser(dn, notifyStatus, permission), {
    mutationKey: 'lockUserReq',
    onSuccess: () => {
      openNotification(
        'success',
        clickedUser?.googleId
          ? `Successfully!! It may take few minutes to enable and disable the account on Third party apps.`
          : `Successfully!!`
      );
    },
    onError: () => openNotification('error', `Failed !!`),
  });
  const [loading, setLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const isFetching = useRef(false);

  useEffect(() => {
    if (
      Array.isArray(usersView) &&
      usersView.length === 0 &&
      (profile?.permission !== 'EndUser' || data?.permission !== 'EndUser') &&
      !usersLoaded &&
      !isFetching.current
    ) {
      setLoading(true);
      isFetching.current = true;
      getUsers()
        .then((response) => {
          if (response?.data?.length > 0) {
            setLoading(false);
          } else {
            setLoading(false);
            setUsersLoaded(true);
          }
        })
        .catch(() => {
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
          isFetching.current = false;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersView, getUsers, profile, usersLoaded]);

  if (
    profile?.permission === 'EndUser' ||
    data?.permission === 'EndUser' ||
    profile?.permission === 'Manager' ||
    data?.permission === 'Manager'
  ) {
    history.push(handleCompanyUrl('/admin/profile'));
  }
  useAppPreloader(loading || deleteUserReq.isLoading || lockUserReq.isLoading);
  const rows = useMemo(() => {
    return usersView ? usersView : [];
  }, [usersView]);
  const [pageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => <PersonIcon style={{ color: 'grey' }} />,
    },
    // {
    //   field: 'createtimestamp',
    //   headerName: 'CREATED',
    //   flex: 0.9,
    //   renderCell: (params: GridCellParams) => (
    //     <Tooltip title={params.row.createtimestamp ? toFormatDateTime(params.row.createtimestamp.toString()) : 'N/A'} placement='bottom'>
    //       <div style={{ display: 'flex', gap: '10px' }}>{params.row.createtimestamp ? toFormatDateTime(params.row.createtimestamp.toString()) : 'N/A'}</div>
    //     </Tooltip>
    //   ),
    // },
    // {
    //   field: 'modifytimestamp',
    //   headerName: 'UPDATED',
    //   flex: 0.9,
    //   renderCell: (params: GridCellParams) => (
    //     <Tooltip title={params.row.modifytimestamp ? toFormatDateTime(params.row.modifytimestamp.toString()) : 'N/A'} placement='bottom'>
    //       <div style={{ display: 'flex', gap: '10px' }}>{params.row.modifytimestamp ? toFormatDateTime(params.row.modifytimestamp.toString()) : 'N/A'}</div>
    //     </Tooltip>
    //   ),
    // },
    {
      field: 'name',
      headerName: 'FULL NAME',
      flex: 0.8,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={(params.row.givenname || '') + ' ' + (params.row.sn || '')}>
          <div>
            <span>
              {params.row.givenname} {params.row.sn}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'uid',
      headerName: endUserConfigSettings?.userId === null || endUserConfigSettings?.userId === '' ? 'User Id' : userId,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <Tooltip title={value || ''} placement='bottom'>
            <div style={{ display: 'flex', gap: '10px' }}>
              <span onClick={() => history.replace(handleCompanyUrl('/user/view'))} style={{ color: 'blue' }}>
                {value}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'mail',
      headerName: 'EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <Tooltip title={value || ''} placement='bottom'>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img src={EmailIcon} alt='' />
              <span>{value}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'googleId',
      headerName: 'SYNC',
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        const appKeys = ['googleId', 'zoomId', 'microsoftId', 'githubId'];
        const connectedApps = appKeys.filter((key) => params.row[key]);
        const connectedCount = connectedApps.length;
        const totalApps = appKeys.length;
        const appNamesMap: Record<string, string> = {
          googleId: 'Google Workspace',
          zoomId: 'Zoom Workspace',
          microsoftId: 'Microsoft Workspace',
          githubId: 'GitHub Workspace',
        };
        const tooltipTitle =
          connectedCount > 0 ? connectedApps.map((app) => appNamesMap[app]).join(', ') : 'Not Connected';
        const greenValue = (connectedCount / totalApps) * 100;
        const greyValue = 100 - greenValue;

        return (
          <Tooltip title={tooltipTitle} placement='bottom'>
            <div style={{ display: 'flex', position: 'relative', alignItems: 'center' }}>
              {connectedCount > 0 ? (
                <>
                  <CircularProgress
                    variant='determinate'
                    value={100}
                    size={40}
                    style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                  />
                  <CircularProgress
                    variant='determinate'
                    value={greyValue}
                    size={40}
                    style={{ color: 'grey', position: 'absolute', zIndex: 2 }}
                  />
                  <CheckCircleIcon
                    style={{
                      color: 'green',
                      fontSize: '24px',
                      zIndex: 3,
                      margin: '8px',
                    }}
                  />
                </>
              ) : (
                <>
                  <CircularProgress
                    variant='determinate'
                    value={100}
                    size={40}
                    style={{ color: 'grey', position: 'absolute', zIndex: 1 }}
                  />
                  <CloseIcon
                    style={{
                      color: 'red',
                      fontSize: '24px',
                      zIndex: 2,
                      margin: '8px',
                    }}
                  />
                </>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 160,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => (
        <>
          <Switcher
            checked={!(!!params.row.locked as boolean)}
            onClick={() =>
              setOpenConfirmLockDialog({
                open: true,
                action: () => lockUserReq.mutate(params.row.dn),
                lockStatus: params.row.locked,
              })
            }
          />
          <Tooltip title='View User'>
            <IconButton size='small' onClick={() => history.replace(handleCompanyUrl('/user/view'))}>
              <PreviewIcon style={{ color: orange[500] }} />
            </IconButton>
          </Tooltip>
          <Tooltip title='Edit User' placement='bottom'>
            <IconButton size='small' onClick={() => history.push(handleCompanyUrl('/user/edit'))}>
              <EditIcon style={{ fill: '#FF9800' }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size='small'
            onClick={() => {
              setOpenConfirmDeleteDialog({
                open: true,
                action: () => deleteUserReq.mutate(params.row.dn),
                appStatus: false,
              });
            }}
          >
            <Tooltip title='Deleted User'>
              <DeleteIcon style={{ fill: '#F44336' }} />
            </Tooltip>
          </IconButton>
        </>
      ),
    },
  ];
  const formattedRows = rows.map((row: { modifytimestamp: string | null; createtimestamp: string | null }) => ({
    ...row,
  }));
  const SaveCkickedUser = (dn: any) => {
    const dataOfUser = { ...usersView.find((user: { dn: any }) => user.dn === dn) };
    localStorage.setItem('ClickedUser', JSON.stringify(dataOfUser));
    setClickedUser(dn);
  };
  return (
    <>
      <Container maxWidth='xl' className={classes.container}>
        <Grid className={classes.infoContainer} container alignItems='stretch' spacing={3}>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.new_users_created_this_week ?? 0}</p>
              <p className={classes.title}>
                New Users
                <br />
                This Week
              </p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.new_users_created_this_month ?? 0}</p>
              <p className={classes.title}>
                New Users
                <br />
                This Month
              </p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.users_synced_this_week ?? 0}</p>
              <p className={classes.title}>
                Users Synced
                <br />
                This Week
              </p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.users_synced_this_month ?? 0}</p>
              <p className={classes.title}>
                Users Synced
                <br />
                This Month
              </p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.users_deleted_this_week ?? 0}</p>
              <p className={classes.title}>
                Users Deleted
                <br />
                This Week
              </p>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>{appStatistics?.users_deleted_this_month ?? 0}</p>
              <p className={classes.title}>
                Users Deleted
                <br />
                This Month
              </p>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      {/* <Container maxWidth='xl' className={classes.container}>

        <Grid className={classes.infoContainer} container alignItems='stretch' spacing={3}>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>2</p>
              <p className={classes.title}>Departments<br />Provisioned</p>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>6</p>
              <p className={classes.title}>Groups<br />Provisioned</p>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value}>7</p>
              <p className={classes.title}>Apps<br />Provisioned</p>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value} style={{ fontSize: 19 }}>
                9
              </p>
              <p className={classes.title}>Last<br />Login</p>

            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Paper className={classes.gridPaper}>
              <p className={classes.value} style={{ fontSize: 22 }}>10</p>
              <p className={classes.title}>Last<br />Location</p>
            </Paper>
          </Grid>

        </Grid>

      </Container> */}
      {profile?.permission === 'EndUser' || profile?.permission === 'Manager' ? (
        <Paper className={classes.dataGrid} elevation={0}>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h3>Welcome, End User!</h3>
            <p>You can manage your profile and view your information.</p>
            <p>To update your profile, please go to Profile section.</p>
          </div>
        </Paper>
      ) : (
        <Paper className={classes.dataGrid} elevation={0}>
          <div
            style={{
              minWidth: isMobile ? 900 : '100%',
              overflowX: 'auto',
            }}
          >
            <DataGrid
              className={classes.noBorder}
              autoHeight
              rows={formattedRows}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={rowsPerPageOptions}
              pagination
              page={page}
              onPageChange={(params) => setPage(params.page ?? 0)}
              onCellClick={(event: GridCellParams) => SaveCkickedUser(event.row.dn)}
            />
          </div>
        </Paper>
      )}
      <ConfirmActionDialog
        label='Are you sure you want to delete this user?'
        appnotify={appStatus}
        appnotifyStatus={setAppStatus}
        buttonLabel='Delete'
        openModal={openConfirmDeleteDialog.open}
        action={openConfirmDeleteDialog.action}
        reSyncProvider={clickedUser?.reSyncProvider || []}
        gitStatus={clickedUser?.gitStatus || ''}
        handleClose={() => {
          setOpenConfirmDeleteDialog({
            open: false,
            action: undefined,
          });
          setAppStatus(false);
        }}
      />
      <ConfirmActionDialog
        label={openConfirmLockDialog.lockStatus === true ? 'Unlock User' : 'Lock User'}
        notify={notifyStatus}
        notifyStatus={setNotifyStatus}
        buttonLabel='Confirm'
        openModal={openConfirmLockDialog.open}
        action={openConfirmLockDialog.action}
        gitStatus={clickedUser?.gitStatus || ''}
        handleClose={() => {
          setOpenConfirmLockDialog({
            open: false,
            action: undefined,
          });
          setNotifyStatus(true);
        }}
      />
    </>
  );
});
